/**
 * MultaResultCard.jsx — Versión rediseñada: tabs internos + header compacto.
 *
 * Cambio principal: en lugar de un único stack vertical largo (header + mensaje +
 * "qué reduce" + disclaimer = ~800px de alto), ahora muestra un header compacto
 * + 3 tabs (Tu situación · Cómo reducir · Disclaimer). Reduce el alto inicial
 * a ~360px sin perder información.
 */
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertTriangle,
  ShieldCheck,
  FileText,
  Info,
  Building2,
  ChevronDown,
  Sparkles,
} from "lucide-react";

const CAT_COLOR = {
  leve: { bg: "from-amber-500 to-amber-600", chip: "bg-amber-500", text: "text-amber-50", border: "border-amber-200" },
  grave: { bg: "from-orange-500 to-orange-700", chip: "bg-orange-500", text: "text-orange-50", border: "border-orange-200" },
  gravisima: { bg: "from-rose-600 to-rose-800", chip: "bg-rose-500", text: "text-rose-50", border: "border-rose-200" },
};

const TABS = [
  { id: "situacion", label: "Tu situación" },
  { id: "reducir", label: "Cómo reducir" },
  { id: "disclaimer", label: "Detalle legal" },
];

export default function MultaResultCard({ pyme, noPyme, resultado, withPymeToggle = false, compact = false }) {
  const [isPyme, setIsPyme] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [tab, setTab] = useState("situacion");
  const active = withPymeToggle ? (isPyme ? pyme : noPyme) : resultado;
  if (!active) return null;

  const cat = (active.categoria || "leve").toLowerCase();
  const palette = CAT_COLOR[cat] || CAT_COLOR.leve;
  const isAmonestacion = active.escenario === "amonestacion_pyme_primera_vez";

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="multa-result-card"
      className={`rounded-2xl overflow-hidden ${compact ? "" : "shadow-lg"} border border-slate-200 bg-white`}
    >
      {/* Header compacto */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        data-testid="multa-header-toggle"
        className={`w-full text-left bg-gradient-to-br ${palette.bg} text-white px-5 py-4 sm:px-6 sm:py-5`}
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`${palette.chip} bg-black/25 text-white px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 shrink-0`}>
              <AlertTriangle className="w-3 h-3" />
              {active.categoria_label || "Leve"}
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-mono uppercase tracking-[0.16em] opacity-75 mb-0.5">Exposición estimada</p>
              <h3
                data-testid="multa-monto"
                className="font-display text-xl sm:text-2xl font-bold leading-tight tracking-tight truncate"
              >
                {isAmonestacion ? "Amonestación escrita" : `${active.monto_min_fmt} – ${active.monto_max_fmt}`}
              </h3>
            </div>
          </div>
          <ChevronDown
            className={`w-5 h-5 opacity-80 transition-transform shrink-0 ${expanded ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </div>

        {withPymeToggle && (
          <div className="mt-4 inline-flex bg-black/25 rounded-lg p-0.5 text-[11px] font-semibold" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              data-testid="toggle-pyme-yes"
              onClick={(e) => { e.stopPropagation(); setIsPyme(true); }}
              className={`px-3 py-1.5 rounded-md transition ${isPyme ? "bg-white text-slate-900" : "text-white/85 hover:text-white"}`}
            >
              Soy PYME · 1ra vez
            </button>
            <button
              type="button"
              data-testid="toggle-pyme-no"
              onClick={(e) => { e.stopPropagation(); setIsPyme(false); }}
              className={`px-3 py-1.5 rounded-md transition ${!isPyme ? "bg-white text-slate-900" : "text-white/85 hover:text-white"}`}
            >
              No-PYME / reincidente
            </button>
          </div>
        )}
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            {/* Tabs */}
            <div className="flex border-b border-slate-200 bg-slate-50/60" role="tablist">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`multa-tab-${t.id}`}
                  className={`relative px-4 sm:px-5 py-3 text-xs sm:text-sm font-semibold transition-colors ${
                    tab === t.id ? "text-[#1E3A8A]" : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {t.label}
                  {tab === t.id && (
                    <motion.div
                      layoutId="multa-tab-indicator"
                      className="absolute inset-x-3 -bottom-px h-0.5 bg-[#1E3A8A]"
                    />
                  )}
                </button>
              ))}
            </div>

            <div className="p-5 sm:p-6">
              {tab === "situacion" && (
                <div className="space-y-3">
                  {isAmonestacion ? (
                    <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex items-start gap-3">
                      <ShieldCheck className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="font-semibold text-emerald-900 text-sm">Te aplica AMONESTACIÓN, no multa</p>
                        <p className="mt-1 text-sm text-emerald-800">{active.mensaje_principal}</p>
                        {active.advertencia_secundaria && (
                          <p className="mt-2 text-xs text-emerald-700/90">{active.advertencia_secundaria}</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 flex items-start gap-3">
                      <FileText className="w-5 h-5 text-slate-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="font-semibold text-slate-900 text-sm">{active.articulo}</p>
                        <p className="mt-1 text-sm text-slate-700">{active.mensaje_principal}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {tab === "reducir" && (
                <div>
                  <h4 className="font-semibold text-slate-900 text-sm inline-flex items-center gap-2 mb-3">
                    <Building2 className="w-4 h-4 text-[#1E3A8A]" /> Qué reduce tu exposición
                  </h4>
                  <ul className="text-sm text-slate-700 space-y-2">
                    <li className="flex items-start gap-2"><span className="text-emerald-500 mt-0.5">✓</span> <span><strong>Modelo de Prevención de Infracciones (MPI)</strong> certificado ante la APDP</span></li>
                    <li className="flex items-start gap-2"><span className="text-emerald-500 mt-0.5">✓</span> <span><strong>Autodenuncia voluntaria</strong> antes que inicie la investigación</span></li>
                    <li className="flex items-start gap-2"><span className="text-emerald-500 mt-0.5">✓</span> <span><strong>Colaboración sustancial</strong> con la Agencia durante el procedimiento</span></li>
                    <li className="flex items-start gap-2"><span className="text-emerald-500 mt-0.5">✓</span> <span><strong>Política de Privacidad vigente</strong>, RAT actualizado y DPA con tus encargados</span></li>
                  </ul>
                  <div className="mt-4 rounded-lg bg-[#1E3A8A]/5 border border-[#1E3A8A]/15 px-3 py-2.5 text-xs text-[#1E3A8A] inline-flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5" />
                    El Módulo Pro genera estos documentos en 30 minutos.
                  </div>
                </div>
              )}

              {tab === "disclaimer" && (
                <div className="rounded-xl bg-blue-50 border border-blue-200 p-4 flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-blue-900 leading-relaxed">
                    <strong>Importante:</strong> {active.disclaimer || "Esta estimación es educativa y no constituye una determinación legal. El monto real lo define la APDP caso a caso según proporcionalidad, intencionalidad y daño causado. Si hay múltiples hallazgos podrían aplicar infracciones adicionales por separado."}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
